import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Button, Paper, Select } from '@material-ui/core';
import EventIcon from '@material-ui/icons/Event';
import AddIcon from '@material-ui/icons/Add';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import ConfirmationModal from '../../components/ConfirmationModal';
import MainContainer from '../../components/MainContainer';
import KanbanBoard from './KanbanBoard';

const useStyles = makeStyles(theme => {
  // Borda das pills do header no mockup: rgba(255,255,255,0.1) (entre o
  // border 0.08 e o borderStrong 0.16 dos tokens).
  const pillBorder =
    theme.palette.type === 'dark'
      ? 'rgba(255,255,255,0.1)'
      : theme.palette.aeros.border;
  return {
    header: {
      minHeight: 60,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
      padding: '0 28px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerTitle: {
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: theme.palette.text.primary,
    },
    headerActions: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
    },
    pill: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      background: theme.palette.aeros.surface,
      border: `1px solid ${pillBorder}`,
      borderRadius: 10,
      padding: '8px 12px',
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    pillIcon: {
      fontSize: 16,
      color: theme.palette.text.secondary,
    },
    pillSelect: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      '& .MuiNativeSelect-select': {
        padding: 0,
        paddingRight: 20,
        background: 'transparent',
        '&:focus': { background: 'transparent' },
      },
      '& .MuiNativeSelect-icon': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        top: 'calc(50% - 9px)',
      },
    },
    dateField: {
      background: 'transparent',
      border: 'none',
      outline: 'none',
      padding: 0,
      fontFamily: 'inherit',
      fontSize: 12.5,
      color: theme.palette.text.primary,
      colorScheme: theme.palette.type,
    },
    dateSep: {
      color: theme.palette.text.secondary,
    },
    ghostButton: {
      borderRadius: 10,
      padding: '6px 14px',
      fontSize: 13,
      fontWeight: 600,
      borderColor: theme.palette.aeros.borderStrong,
    },
    addButton: {
      borderRadius: 10,
      padding: '6px 16px',
      fontSize: 13,
      '& .MuiSvgIcon-root': { fontSize: 17 },
    },
    mainPaper: {
      flex: 1,
      display: 'flex',
      padding: '24px 28px',
      overflow: 'hidden',
      borderRadius: 0,
      backgroundColor: theme.palette.aeros.background,
    },
  };
});

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);
  // Coluna aguardando confirmacao de exclusao (a coluna e a propria tag).
  const [deletingLane, setDeletingLane] = useState(null);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar ordem das lanes do banco de dados
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async (fetchedTags = tags) => {
    try {
      const { data } = await api.get('/ticket/kanban', {
        params: {
          queueIds: JSON.stringify(queueIds),
          startDate: startDate,
          endDate: endDate,
        },
      });
      setTickets(data.tickets);
      organizeLanes(fetchedTags, data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (['create', 'update', 'delete'].includes(data.action)) {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = updatedTicket => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
  };

  const getOpportunityValue = (ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  };

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets) => {
    const sortedTickets = [...fetchedTickets];

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => ticket.tags.length === 0
    );

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          ticket.tags.some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
        };
      }),
    ];

    // Aplicar ordem personalizada se existir
    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      
      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      
      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));
      
      setLanes(orderedLanes);
    } else {
      setLanes(lanesData);
    }
  };

  useEffect(() => {
    if (!loadingLaneOrder) {
      organizeLanes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, sortOrder, laneOrder, loadingLaneOrder]);

  // Excluir a coluna e excluir a tag Kanban: os tickets perdem a etiqueta e
  // voltam para a coluna padrao ("Em aberto"). Mesmo endpoint da /tagsKanban.
  const handleDeleteLane = async () => {
    if (!deletingLane) return;
    try {
      await api.delete(`/tags/${deletingLane.id}`);
      toast.success(i18n.t('tagsKanban.toasts.deleted'));
      fetchTags();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao excluir a coluna.');
    }
    setDeletingLane(null);
  };

  const handleCardMove = async (ticketId, targetLaneId) => {
    ticketId = parseInt(ticketId, 10);
    try {
      await api.delete(`/ticket-tags/${ticketId}`);

      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        toast.success('Ticket Tag atualizado com sucesso!');
      } else {
        toast.success('Ticket Tag removido!');
      }

      fetchTickets();
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Verificar se o usuário é admin
    if (user.profile !== 'admin') {
      toast.error('Apenas administradores podem reordenar as lanes do Kanban');
      return;
    }

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      await setKanbanLaneOrder(newLaneOrder);
      toast.success('Ordem das lanes atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
      toast.error('Erro ao salvar ordem das lanes');
    }
  };

  return (
    <MainContainer>
      <div className={classes.header}>
        <div className={classes.headerTitle}>{i18n.t('Kanban')}</div>
        <div className={classes.headerActions}>
          <div className={classes.pill}>
            <span>Ordenar:</span>
            <Select
              native
              disableUnderline
              value={sortOrder}
              onChange={handleSortOrderChange}
              className={classes.pillSelect}
              inputProps={{
                name: 'sortOrder',
                id: 'sort-order-select',
                'aria-label': 'Ordenar por',
              }}
            >
              <option value="ticketNumber">Número do ticket</option>
              <option value="lastMessageTime">Última mensagem</option>
              <option value="valorDesc">Valor (maior para menor)</option>
            </Select>
          </div>
          <div className={classes.pill}>
            <EventIcon className={classes.pillIcon} />
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              className={classes.dateField}
              aria-label="Data de início"
            />
            <span className={classes.dateSep}>–</span>
            <input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              className={classes.dateField}
              aria-label="Data de fim"
            />
          </div>
          <Button
            variant="outlined"
            onClick={handleSearchClick}
            className={classes.ghostButton}
          >
            Buscar
          </Button>
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddColumnClick}
                className={classes.addButton}
              >
                Adicionar colunas
              </Button>
            )}
          />
        </div>
      </div>
      <ConfirmationModal
        title={
          deletingLane &&
          `${i18n.t('tagsKanban.confirmationModal.deleteTitle')} (${deletingLane.title})`
        }
        open={Boolean(deletingLane)}
        onClose={() => setDeletingLane(null)}
        onConfirm={handleDeleteLane}
      >
        {i18n.t('tagsKanban.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <Paper elevation={0} square className={classes.mainPaper}>
        <KanbanBoard
          lanes={lanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          isAdmin={user.profile === 'admin'}
          onDeleteLane={lane => setDeletingLane(lane)}
        />
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
